import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldControl } from '@angular/material/form-field';
import { combineLatest, debounceTime, distinctUntilChanged, filter, fromEvent, map, merge, skip, take, takeUntil, tap } from 'rxjs';
import { MatFormFieldControlImpl } from '../../ng-material-impl/mat-form-field-control.impl';
import { AutocompleteFormControlService } from '../autocomplete-form-control.service';

@Component({
  selector: 'ss-autocomplete-control',
  templateUrl: './autocomplete-control.component.html',
  styleUrls: ['./autocomplete-control.component.css'],
  providers: [{ provide: MatFormFieldControl, useExisting: AutocompleteControlComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutocompleteControlComponent extends MatFormFieldControlImpl<string> implements OnInit {

  @ViewChild('inputRef')
  private _inputRef!: ElementRef<HTMLInputElement>

  @ViewChild('autoCompleteRef', { read: MatAutocomplete })
  private _autoCompleteRef: MatAutocomplete

  readonly listItemsSubject = this._autocompleteFormControlService.listItemsSubject;
  readonly fetchedDataSubject = this._autocompleteFormControlService.fetchedDataSubject;

  readonly valueExprSubject = this._autocompleteFormControlService.valueExprSubject;
  get valueExpr() {
    return this.valueExprSubject.value || '';
  }

  readonly dataSourceSubject = this._autocompleteFormControlService.dataSourceSubject;
  readonly displayExprSubject = this._autocompleteFormControlService.displayExprSubject;
  readonly templateSubject = this._autocompleteFormControlService.templateSubject;
  readonly selectedItemSubject = this._autocompleteFormControlService.selectedItemSubject;
  readonly displayObs = this._autocompleteFormControlService.displayObs;

  readonly displayWithObs = this.displayExprSubject
    .pipe(
      map(displayExpr => (data: any) => {

        if (!displayExpr) {
          return data.toString();
        }

        if (typeof displayExpr === 'string') {
          return (data[displayExpr] || '').toString();
        }

        return displayExpr(data);
      })
    )

  override get shouldLabelFloat() {
    return this.focused || Boolean(this.searchValueSubject.value)
  }

  readonly valueSubject = this._formControlService.valueSubject;
  readonly searchValueSubject = this._autocompleteFormControlService.searchValueSubject;
  readonly locallyFilteredDataSubject = this._autocompleteFormControlService.locallyFilteredDataSubject;
  readonly matOptionTemplateRefSubject = this._autocompleteFormControlService.matOptionTemplateRefSubject;
  readonly valueIsDisplaySubject = this._autocompleteFormControlService.valueIsDisplaySubject;

  constructor(
    private _autocompleteFormControlService: AutocompleteFormControlService
  ) {
    super('autocomplete');

  }

  ngOnInit(): void {

    this.valueSubject
      .pipe(
        takeUntil(this._destroy),
        distinctUntilChanged(),
      )
      .subscribe((value) => {


        const selectedItem = this.selectedItemSubject.value;

        const confirmedValue = selectedItem && selectedItem[this.valueExpr];

        if (confirmedValue !== value) {
          this._autocompleteFormControlService.confirmValue(value)
        }
      })

    combineLatest([
      this.selectedItemSubject,
      this.valueIsDisplaySubject
    ])
      .pipe(
        skip(1),
        filter(([_, valueIsDiplay]) => !valueIsDiplay),
        takeUntil(this._destroy),
        distinctUntilChanged())
      .subscribe(([selectedItem]) => {

        const valueExpr = this.valueExpr;
        const selectedItemValue = selectedItem && selectedItem[valueExpr];
        const value = this.valueSubject.value;

        if (selectedItemValue != value) {
          this.valueSubject.next(selectedItemValue)
        }
      })
  }

  ngAfterViewInit() {

    this._autocompleteFormControlService.autocompleteSubject.next(this._autoCompleteRef);

    const keyupObs = fromEvent(this._inputRef.nativeElement, 'keyup')
    const changeObs = fromEvent(this._inputRef.nativeElement, 'change')

    this._formControlService.htmlElementSubject.next(this._inputRef.nativeElement);

    const valueChangedObs = merge(keyupObs, changeObs).pipe(
      map((evParams) => {
        const inputElement = evParams.target as HTMLInputElement;
        const value = inputElement.value;
        return value;
      }),
      distinctUntilChanged()
    )

    combineLatest([valueChangedObs, this.valueIsDisplaySubject])
      .pipe(
        tap(([value, valueIsDisplay]) => {

          if (valueIsDisplay) {
            this.valueSubject.next(value)
          }
        }),
        takeUntil(this._destroy),
        debounceTime(200)
      )
      .subscribe(([value]) => {

        this.searchValueSubject.next(value);
      })

    fromEvent(this._inputRef.nativeElement, 'focus')
      .pipe(take(1), takeUntil(this._destroy))
      .subscribe(() => {
        this._autocompleteFormControlService.focusedSubject.next(true)
      })
  }


  onContainerClick(event: MouseEvent): void {
    this._inputRef.nativeElement.focus();
  }

  handleOptSelected(evParams: MatAutocompleteSelectedEvent) {
    const value = evParams.option.value;

    this.selectedItemSubject.next(value);

  }

  ngOnDestroy() {

    this._destroy.next();
    this._destroy.complete();

  }

}


